/**
 * Centralized query key factory for TanStack Query.
 *
 * HIERARCHICAL STRUCTURE: invalidating a parent key (e.g., ['event', id])
 * automatically invalidates all child keys (e.g., ['event', id, 'speakers']).
 *
 * CONVENTION FOR NEW FEATURES:
 * 1. Add key here following the hierarchy
 * 2. Add fetch function to queryFns.ts
 * 3. Add mutation invalidation to mutations.ts
 * New sub-keys under ['event', eventId, 'x'] get auto-invalidated on event save.
 */

/**
 * Filter objects are built from URL params and component state, so key order
 * varies between renders. Sort before serialising or the same filter set would
 * produce two different cache keys. Array-valued filters (multi-selects) are
 * sorted too, since the RPC treats them as unordered `IN (...)` membership -
 * a different checkbox order must not fragment the cache. Empty strings are
 * stripped alongside undefined/null so `{search: ''}` and `{}` collapse to the
 * same key, matching the no-op check the RPC itself does on `p_filters->>'search'`.
 * Kept in step by hand with `cleanFilters` in src/lib/queries/bprDashboard.ts,
 * which strips the same values before the RPC call - no import between the two
 * to avoid pulling a BPR-specific concern into this generic module.
 *
 * Exported (not just used internally by `bprDashboard.rows` below) so callers
 * that need to detect "did the filters actually change" - e.g. a page-reset
 * effect - normalise identically to the cache key itself, rather than
 * reimplementing the same key-order/empty-value handling with plain
 * `JSON.stringify` and risking the two disagreeing.
 */
export function stableFilterKey(filters: object): string {
  return JSON.stringify(
    Object.fromEntries(
      Object.entries(filters)
        .filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0))
        .map(([k, v]) => [k, Array.isArray(v) ? [...v].sort() : v])
        .sort(([a], [b]) => a.localeCompare(b)),
    ),
  );
}

export const queryKeys = {
  events: {
    /** List all events (public browsing). NOT a prefix of detail keys. */
    all: ['events'] as const,
    /** Featured content on home/dashboard */
    featured: ['events', 'featured'] as const,
    /** Single event detail + prefix for all sub-data. Consumed by the public
     *  event page, which wraps the row as { course, contentBlocks, ... }. */
    detail: (id: string) => ['event', id] as const,
    /** Builder's own cached copy of the raw content row. Separate key so the
     *  builder's setQueryData writes don't poison `detail`, which the public
     *  page reads as a wrapped shape. */
    editDraft: (id: string) => ['event', id, 'edit-draft'] as const,
    speakers: (id: string) => ['event', id, 'speakers'] as const,
    partners: (id: string) => ['event', id, 'partners'] as const,
    pricing: (id: string) => ['event', id, 'pricing'] as const,
    currency: (id: string) => ['event', id, 'currency'] as const,
    tests: (id: string) => ['event', id, 'tests'] as const,
    surveys: (id: string) => ['event', id, 'surveys'] as const,
    materials: (id: string) => ['event', id, 'materials'] as const,
    moz: (id: string) => ['event', id, 'moz'] as const,
    speakerRegistration: (id: string) => ['event', id, 'speakerRegistration'] as const,
    tabCounts: (id: string) => ['event', id, 'tab-counts'] as const,
    certificates: (id: string) => ['event', id, 'certificates'] as const,
    /** All ticket questions for an event, with their ticket links. */
    ticketQuestions: (id: string) => ['event', id, 'ticket-questions'] as const,
    ticketCertificates: (id: string) => ['event', id, 'ticket-certificates'] as const,
    /** Stored answer counts per ticket question, for the delete warning. */
    ticketQuestionAnswers: (id: string) => ['event', id, 'ticket-question-answers'] as const,
    /** Builder: the event's registration field states, ticket overrides and БПР lock facts. */
    registrationForm: (id: string) => ['event', id, 'registration-form'] as const,
    testQuestions: (testId: string) => ['test', testId, 'questions'] as const,
    surveyQuestions: (surveyId: string) => ['survey', surveyId, 'questions'] as const,
    userTestAttempts: (eventId: string, enrollmentId: string) =>
      ['event', eventId, 'test-attempts', enrollmentId] as const,
    userSurveyCompletions: (eventId: string, enrollmentId: string) =>
      ['event', eventId, 'survey-completions', enrollmentId] as const,
    testHistory: (eventId: string, enrollmentId: string) =>
      ['event', eventId, 'test-history', enrollmentId] as const,
  },
  organizer: {
    content: (userId: string, type: string, filter: string) =>
      ['organizer-content', userId, type, filter] as const,
    contentPrefix: (userId: string) =>
      ['organizer-content', userId] as const,
  },
  dashboard: {
    all: (userId: string) => ['dashboard', userId] as const,
    pending: (userId: string) => ['dashboard', userId, 'pending'] as const,
    history: (userId: string) => ['dashboard', userId, 'history'] as const,
    enrollments: (userId: string) => ['dashboard', userId, 'enrollments'] as const,
    organizer: (userId: string) => ['dashboard', userId, 'organizer'] as const,
  },
  /** "Details needed" per enrollment; prefix ['details'] covers every surface. */
  details: {
    all: ['details'] as const,
    forUser: (userId: string) => ['details', userId] as const,
    enrollment: (enrollmentId: string) => ['details', 'enrollment', enrollmentId] as const,
  },
  enrollment: (userId: string, eventId: string) =>
    ['enrollment', userId, eventId] as const,
  serverNow: ['server-now'] as const,
  /** The organizer's own registration presets, default preset and question library. */
  registration: {
    all: (userId: string) => ['registration', userId] as const,
    presets: (userId: string) => ['registration', userId, 'presets'] as const,
    defaultPreset: (userId: string) => ['registration', userId, 'default-preset'] as const,
    questionTemplates: (userId: string) => ['registration', userId, 'question-templates'] as const,
  },
  /** "Test not included in your ticket" notices; prefix ['test-not-included', userId] covers every surface. */
  testNotIncluded: (userId: string, contentId?: string) =>
    ['test-not-included', userId, contentId ?? 'all'] as const,
  profile: {
    all: ['profile'] as const,
    deletionPreview: () => ['profile', 'deletion-preview'] as const,
    certificateStatusesAll: ['profile', 'certificate-statuses'] as const,
    certificateStatuses: (contentIds: string[]) => ['profile', 'certificate-statuses', ...[...contentIds].sort()] as const,
  },
  speakers: {
    /** Prefix of every browser() entry, whatever the search term */
    browserAll: ['speakers', 'browser'] as const,
    browser: (query?: string) => ['speakers', 'browser', query] as const,
  },
  bprApplications: {
    /** List (own for applicants, all for admins) */
    all: ['bpr-applications'] as const,
    /** Detail incl. payload + history */
    detail: (id: string) => ['bpr-applications', id] as const,
  },
  bprDashboard: {
    /** Prefix - invalidating this refreshes rows, summary and facets together. */
    all: ['bpr-dashboard'] as const,
    rows: (filters: object, sort: string, dir: string, page: number) =>
      ['bpr-dashboard', 'rows', stableFilterKey(filters), sort, dir, page] as const,
    summary: (filters: object) =>
      ['bpr-dashboard', 'summary', stableFilterKey(filters)] as const,
    facets: (filters: object) =>
      ['bpr-dashboard', 'facets', stableFilterKey(filters)] as const,
  },
  /** The superadmin /admin/payments resolution UI (Phase 4, spec Item 8). */
  adminPayments: {
    /** Prefix - invalidating this refreshes both tables together. */
    all: ['admin-payments'] as const,
    refunds: (days: number) => ['admin-payments', 'refunds', days] as const,
    /** `includeResolved` is part of the key (R7): a different filter is a different view, so it must not share a cache entry with the default one. */
    blocked: (includeResolved: boolean) => ['admin-payments', 'blocked', includeResolved] as const,
  },
  /**
   * The organizer-facing CRM (`/dashboard/crm`), scoped per event. Anything
   * an organizer reads about ONE event's payments/refunds belongs here -
   * as opposed to `adminPayments` above, which is the superadmin's
   * cross-event resolution UI.
   */
  crm: {
    /** Prefix - not invalidated as a whole today (each view below is its own fetch), kept for symmetry with adminPayments/bprDashboard. */
    all: ['crm'] as const,
    /** Phase 6 Task 6: the organizer's refund-approval panel on one event's participant list. */
    pendingRefunds: (eventId: string) => ['crm', 'pending-refunds', eventId] as const,
    /** Phase 6 Task 11: one participant's merged payment/refund/audit history, on the CRM participant row. */
    paymentTimeline: (enrollmentId: string) => ['crm', 'payment-timeline', enrollmentId] as const,
    /** Details needed: who still owes details on these contents (badge + filter). Sorted copy, order-independent. */
    /** Prefix of every missingDetails(...) key – invalidated after an organizer action refreshes the CRM. */
    missingDetailsAll: ['crm', 'missing-details'] as const,
    missingDetails: (contentIds: string[]) => ['crm', 'missing-details', ...[...contentIds].sort()] as const,
  },
};
